import {
  any, number, object, string,
} from 'prop-types';
import { useRef, useState } from 'react';

import { QuestionCircleOutlined, UploadOutlined } from '@ant-design/icons';
import {
  Button,
  Col, Form, Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Upload,
} from 'antd';

import { ChevronSVG } from 'components/iconsSVG/chevron';
import { CloseModalSVG } from 'components/iconsSVG/close';
import { registerPelotas } from 'actions/users';
import HeadSEO from 'components/base/headSEO';
import FormInput from 'components/formInput';
import Layout from 'components/layout';
import MyAccountLayout from 'components/layout/myaccount';
import { useI18n } from 'context/i18n';
import { useUser } from 'context/user';
import { initCMS } from 'utils/cms';

import { useValidate } from 'hooks';
import styles from './style.module.css';

const { Option } = Select;

const HELP_URL = {
  STORE: '/assets-new/register-order-store.jpg',
  DATE: '/assets-new/register-order-date.jpg',
  IMPORT: '/assets-new/register-order-import.jpg',
  TICKET_NUMBER: '/assets-new/register-order-ticket-num.jpg',
  PRODUCT: '/assets-new/register-order-product.jpg',
};
const MAX_PRODUCTS = 5;
const SIZE_LIMIT_MB = 1;

function getLastMonthsAndDays(numberOfMonths = 3) {
  const currentDate = new Date();
  const lastMonths = [];
  const daysInMonth = {};
  for (let i = 0; i < numberOfMonths; i++) {
    const month = currentDate.getMonth() - i;
    const year = currentDate.getFullYear() - (month < 0 ? 1 : 0);
    const formattedMonth = (((month % 12) + 12) % 12) + 1;
    const daysOfCurrentMonth = new Date(year, month + 1, 0).getDate();
    daysInMonth[formattedMonth.toString().padStart(2, '0')] = Array.from({ length: daysOfCurrentMonth }, (_, dayIndex) => String(dayIndex + 1).padStart(2, '0'));
    const dateString = `${formattedMonth.toString().padStart(2, '0')} - ${year}`;
    lastMonths.push(dateString);
  }
  return { lastMonths, daysInMonth };
}

function RegisterPelotas({
  defaultProps, locale,
}) {
  const { t, profileData } = useI18n();
  const { userData } = useUser();
  const validate = useValidate();
  const [ isLt1M, setIsLt1M ] = useState(true);
  const [ selectedMonth, setSelectedMonth ] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [ selectedRadio, setSelectedRadio ] = useState('camperStore');
  const [ products, setProducts ] = useState(1);
  const [ modalHelp, setModalHelp ] = useState(null);
  const [ modalHelpVisible, setModalHelpVisible ] = useState(false);
  const [ loading, setLoading ] = useState(false);
  const [ errorRq, setErrorRq ] = useState(null);
  const [ successRq, setSuccessRq ] = useState(false);
  const [ RegisterForm ] = Form.useForm();
  const UploadRef = useRef();

  const { lastMonths, daysInMonth } = getLastMonthsAndDays();

  const beforeUpload = (file) => {
    const isValidFile = file.size / 1024 / 1024 < SIZE_LIMIT_MB;
    let isValidFileList = true;
    UploadRef.current.fileList.forEach((item) => {
      if (!(item.size / 1024 / 1024 < SIZE_LIMIT_MB)) {
        isValidFileList = false;
      }
    });
    setIsLt1M(isValidFile && isValidFileList);
  };

  const onRemove = (file) => {
    const { fileList } = UploadRef.current;
    let isValidFileList = true;
    for (let i = 0; i < fileList.length; i++) {
      if (!(fileList[i].size / 1024 / 1024 < SIZE_LIMIT_MB) && file.uid !== fileList[i].uid) {
        isValidFileList = false;
      }
    }
    setIsLt1M(isValidFileList);
  };

  const onChangeMonth = (value) => {
    setSelectedMonth(value.split(' - ')[0]);
    RegisterForm.setFieldsValue({ day: '01' });
  };

  const onClickHelp = (url) => {
    setModalHelp(url);
    setModalHelpVisible(true);
  };

  const onFinishRegisterForm = async (values) => {
    setLoading(true);
    const ticketFiles = [];
    if (values.ticket) {
      if (values.ticket.fileList && values.ticket.fileList.length > 0) {
        values.ticket.fileList.forEach((file) => ticketFiles.push(file.originFileObj));
      }
    }
    const date = `${values.day}/${values.month.split(' - ').join('/')}`;
    const amount = values.import;
    const payload = selectedRadio === 'camperStore' ? {
      email: userData.account?.email || '',
      type: 'Camper Store',
      date,
      amount,
      store: values.store,
      ticket: ticketFiles,
      profile: locale,
    } : {
      email: userData.account?.email || '',
      type: 'Others',
      date,
      amount: amount || 0,
      store: values.storePoint,
      ticket: ticketFiles,
      profile: locale,
    };
    if (selectedRadio !== 'camperStore') {
      for (let i = 0; i < products; i++) {
        payload[`product${i}`] = values[`product-${i}`];
      }
    }
    try {
      await registerPelotas(payload);
      setErrorRq(null);
      setSuccessRq(true);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setErrorRq(t('pedido', 'pedido.api.modal.contenido', 'We are sorry. The request could not be processed.'));
      setLoading(false);
    }
  };

  if (successRq) {
    return (
      <Layout {...defaultProps} breadcrumbs={false}>
        <HeadSEO description="Your order" pageName={'RegisterPelotas'} />
        <MyAccountLayout withCategoriesLayout={false} withCategories={false} formWrapperStyles={styles.formWrapper}>
          <main>
            <div className={styles.container}>
              <p className={styles.successText}>{t('newsletter', 'purchase.registered')}</p>
              <p className={styles.accountLink}><a href={`/${locale}/myaccount`}>{t('mi.cuenta', 'mi.cuenta', 'My Account')}</a></p>
            </div>
          </main>
        </MyAccountLayout>
      </Layout>
    );
  }

  return (
    <Layout {...defaultProps} breadcrumbs={false}>
      <HeadSEO description="Your order" pageName={'RegisterPelotas'} />
      <div className={styles.headerWrapper}>
        <h1>{t('newsletter', 'register.pelotas.title')}</h1>
        <div className={styles.description} dangerouslySetInnerHTML={{ __html: t('newsletter', `register.purchase.pelotas.text.${profileData.storeId || 'T176'}`) }}></div>
      </div>
      <MyAccountLayout withCategoriesLayout={false} withCategories={false} formWrapperStyles={styles.formWrapper}>
        <main>
          <div className={styles.container}>
            <Form
              form={RegisterForm}
              layout="vertical"
              initialValues={{
                day: String(new Date().getDate()).padStart(2, '0'),
                month: `${String(new Date().getMonth() + 1).padStart(2, '0')} - ${new Date().getFullYear()}`,
                storeType: 'camperStore',
              }}
              name="register"
              onFinish={onFinishRegisterForm}
              onFinishFailed={(errorInfo) => {
                console.error('Failed:', errorInfo);
              }}
              requiredMark='optional'
              scrollToFirstError
              className={`${styles.registerForm} customForm`}
            >
              <Row gutter={[ 8, 0 ]}>
                <Form.Item name="storeType">
                  <Radio.Group onChange={(e) => setSelectedRadio(e.target.value)}>
                    <Radio value="camperStore">
                      {t('newsletter', 'tienda.camper')}
                    </Radio>

                    <Radio value="otherStore">
                      {t('newsletter', 'otra.tienda')}
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Row>
              <Row gutter={[ 8, 0 ]}>
                {selectedRadio === 'camperStore' ?
                  <Col md={12} xs={24}>
                    <QuestionCircleOutlined className="site-form-item-icon" onClick={() => onClickHelp(HELP_URL.STORE)} />
                    <span className={styles.label}>{t('newsletter', 'tienda')}</span>
                    <FormInput
                      name="store"
                      rules={validate('requiredField')}
                      floatLabel=" "
                    >
                      <Input placeholder="" />
                    </FormInput>
                  </Col> :
                  <Col md={12} xs={24}>
                    <QuestionCircleOutlined className="site-form-item-icon" onClick={() => onClickHelp(HELP_URL.STORE)} />
                    <span className={styles.label}>{t('newsletter', 'distribuidor')}</span>
                    <FormInput
                      name="storePoint"
                      rules={validate('requiredField')}
                      floatLabel=" "
                    >
                      <Input placeholder="" />
                    </FormInput>
                  </Col>
                }
                <Col md={12} xs={24} className={styles.uploadWrapper}>
                  <QuestionCircleOutlined className="site-form-item-icon" onClick={() => onClickHelp(HELP_URL.TICKET_NUMBER)} />
                  <span className={styles.label}>{t('newsletter', 'ticket')}</span>
                  <FormInput
                    name="ticket"
                    rules={[
                      {
                        required: true,
                        message: t('validation', 'required.field', 'this field is required'),
                      },
                      {
                        validator: () => (isLt1M ? Promise.resolve() : Promise.reject(new Error(`${t('validation', 'upload.maxium.file.size', '**Maximum file size exceeded')} (${SIZE_LIMIT_MB} MB)`))),
                      },
                    ]}
                  >
                    <Upload
                      maxCount={5}
                      name='ticket'
                      beforeUpload={beforeUpload}
                      onRemove={onRemove}
                      ref={UploadRef}
                    >
                      <Button tabIndex="26" icon={<UploadOutlined />}>{t('newsletter', 'select.archive', 'Select file')}</Button>
                    </Upload>
                  </FormInput>
                </Col>
              </Row>
              <Row gutter={[ 8, 0 ]}>
                <Col md={6} xs={12}>
                  <QuestionCircleOutlined className="site-form-item-icon" onClick={() => onClickHelp(HELP_URL.DATE)} />
                  <span className={styles.label}>{t('newsletter', 'fecha.compra')}</span>
                  <FormInput
                    name="day"
                    rules={validate('requiredField')}
                    floatLabel={t('newsletter', 'dia.vacio')}
                  >
                    <Select
                      suffixIcon ={<ChevronSVG />}
                      virtual={false}
                      dropdownClassName={styles.dropdown}
                    >
                      {
                        daysInMonth && daysInMonth[selectedMonth] &&
                          daysInMonth[selectedMonth].map((item, index) => <Option key={`day-${index}`} value={item}>{item}</Option>)
                      }
                    </Select>
                  </FormInput>
                </Col>
                <Col md={6} xs={12} className={styles.monthSelector}>
                  <FormInput
                    name="month"
                    rules={validate('requiredField')}
                    floatLabel={t('newsletter', 'mes.vacio')}
                  >
                    <Select
                      optionFilterProp="children"
                      suffixIcon ={<ChevronSVG />}
                      virtual={false}
                      dropdownClassName={styles.dropdown}
                      onSelect={(value) => onChangeMonth(value)}
                    >
                      {
                        lastMonths?.map((item, index) => <Option key={`m-${index}`} value={item}>{item}</Option>)
                      }
                    </Select>
                  </FormInput>
                </Col>
                {selectedRadio === 'camperStore' ?
                  <Col md={12} xs={24}>
                    <QuestionCircleOutlined className="site-form-item-icon" onClick={() => onClickHelp(HELP_URL.IMPORT)} />
                    <span className={styles.label}>{t('newsletter', 'importe')} {t('newsletter', 'patron.importe')}</span>
                    <FormInput
                      name="import"
                      rules={validate('requiredField')}
                      floatLabel=" "
                    >
                      <InputNumber min={1} />
                    </FormInput>
                  </Col> :
                  <Col md={12} xs={24} className={styles.productsCol}>
                    <QuestionCircleOutlined className="site-form-item-icon" onClick={() => onClickHelp(HELP_URL.PRODUCT)} />
                    <span className={styles.label}>{t('bolsa', 'producto')}</span>
                    {[ ...Array(products).keys() ].map((n) => (
                      <FormInput
                        key={`product-${n}`}
                        name={`product-${n}`}
                        rules={validate('requiredField')}
                        floatLabel=" "
                      >
                        <Input placeholder="" />
                      </FormInput>
                    ))}
                    <div className={products > 1 ? styles.addProductsDiv : `${styles.addProductsDiv} ${styles.addProductsDivFirstRow}`}>
                      {products > 1 &&
                        <span onClick={() => setProducts(products - 1)}>{t('generico', 'boton.borrar', 'Remove')}</span>
                      }
                      {products + 1 < MAX_PRODUCTS ?
                        <span onClick={() => setProducts(products + 1)}>{t('newsletter', 'anadir.otro.producto')}</span> :
                        <span></span>
                      }
                    </div>
                  </Col>
                }
              </Row>
              {errorRq &&
                <p className={styles.error}>{errorRq}</p>
              }
              <Form.Item className={styles.btnRegister}>
                <Button
                  type="primary"
                  className={styles.btnAddToBag}
                  htmlType="submit"
                  size="large"
                  loading={loading}
                >
                  {t('newsletter', 'registrar')}
                </Button>
              </Form.Item>
            </Form>
          </div>
          <Modal
            centered
            visible={modalHelpVisible}
            onCancel={() => setModalHelpVisible(false)}
            className={styles.modalHelp}
            footer={null}
            closeIcon={<CloseModalSVG />}
          >
            {modalHelp &&
              <img src={modalHelp} />
            }
          </Modal>
        </main>
      </MyAccountLayout>
      <div className={styles.headerWrapper}>
        <p className={styles.conditions}>{t('newsletter', 'landing.registro.pelotas.condiciones')}</p>
      </div>
    </Layout>
  );
}

RegisterPelotas.propTypes = {
  footerProps: any,
  tnmProps: any,
  gtmProps: object,
  ribbon: any,
  reference: string,
  customerReference: string,
  subtotal: number,
  locale: string,
};

RegisterPelotas.i18nPage = 'RegisterPelotas';

export async function getServerSideProps(context) {
  const { locale } = context;
  const { defaultProps } = await initCMS({ context, locale });

  return {
    props: {
      defaultProps,
      locale,
    },
  };
}

export default RegisterPelotas;
